/**
 * @fileoverview useDevConsole composable
 * @description Captures console errors, unhandled exceptions, promise rejections,
 * and network errors (4xx/5xx) for developer debugging in non-production environments.
 */

import { ref, computed, shallowRef } from 'vue'

export type ConsoleEntryType = 'console-error' | 'exception' | 'rejection' | 'network'
export type ConsoleEntrySource = 'Frontend' | 'BFF' | 'Database'

export interface ConsoleEntry {
  id: number
  type: ConsoleEntryType
  source: ConsoleEntrySource
  message: string
  timestamp: Date
  stack?: string
  url?: string
  status?: number
}

export type FilterState = Record<ConsoleEntryType, boolean>

let nextId = 0
let installed = false

const entries = shallowRef<ConsoleEntry[]>([])
const filters = ref<FilterState>({
  'console-error': true,
  exception: true,
  rejection: true,
  network: true,
})
const isExpanded = ref(false)

function addEntry(entry: Omit<ConsoleEntry, 'id' | 'timestamp'>) {
  const newEntry: ConsoleEntry = {
    ...entry,
    id: nextId++,
    timestamp: new Date(),
  }
  entries.value = [...entries.value, newEntry]
}

const DB_ERROR_PATTERNS =
  /\b(constraint|violation|relation|column.*does not exist|duplicate key|foreign key|deadlock|pg_|knex|ECONNREFUSED.*5432|database|sql_error)\b/i

async function detectServerSource(response: Response): Promise<ConsoleEntrySource> {
  try {
    const clone = response.clone()
    const text = await clone.text()
    if (DB_ERROR_PATTERNS.test(text)) return 'Database'
  } catch {
    // Body unreadable — default to BFF
  }
  return 'BFF'
}

function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.stack ?? value.message
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function install() {
  if (installed) return
  installed = true

  // Intercept console.error
  const originalError = console.error
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args)
    const message = args.map(safeStringify).join(' ')
    addEntry({ type: 'console-error', source: 'Frontend', message })
  }

  // Unhandled exceptions
  window.addEventListener('error', (event: ErrorEvent) => {
    addEntry({
      type: 'exception',
      source: 'Frontend',
      message: event.message || 'Unknown error',
      stack: event.error?.stack,
      url: `${event.filename}:${event.lineno}:${event.colno}`,
    })
  })

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason
    const message = safeStringify(reason)
    addEntry({
      type: 'rejection',
      source: 'Frontend',
      message,
      ...(reason instanceof Error && reason.stack ? { stack: reason.stack } : {}),
    })
  })

  // Intercept fetch — HTTP 4xx/5xx are console errors, connection failures are network errors
  const originalFetch = window.fetch
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const url =
      typeof args[0] === 'string'
        ? args[0]
        : args[0] instanceof Request
          ? args[0].url
          : String(args[0])
    try {
      const response = await originalFetch(...args)
      if (response.status >= 400) {
        const source = await detectServerSource(response)
        addEntry({
          type: 'console-error',
          source,
          message: `${response.status} ${response.statusText}`,
          url,
          status: response.status,
        })
      }
      return response
    } catch (err) {
      addEntry({
        type: 'network',
        source: 'BFF',
        message: err instanceof Error ? err.message : 'Network request failed',
        url,
      })
      throw err
    }
  }

  // Intercept XMLHttpRequest — HTTP errors are console errors, connection failures are network
  const originalXHROpen = XMLHttpRequest.prototype.open
  const originalXHRSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
    ;(this as XMLHttpRequest & { _devConsoleUrl: string })._devConsoleUrl = String(url)
    return (originalXHROpen as Function).call(this, method, url, ...rest)
  }
  XMLHttpRequest.prototype.send = function (...args: unknown[]) {
    this.addEventListener('loadend', () => {
      if (this.status >= 400) {
        let source: ConsoleEntrySource = 'BFF'
        try {
          if (DB_ERROR_PATTERNS.test(this.responseText)) source = 'Database'
        } catch {
          // responseText may not be accessible
        }
        addEntry({
          type: 'console-error',
          source,
          message: `${this.status} ${this.statusText}`,
          url: (this as XMLHttpRequest & { _devConsoleUrl: string })._devConsoleUrl,
          status: this.status,
        })
      }
    })
    this.addEventListener('error', () => {
      addEntry({
        type: 'network',
        source: 'BFF',
        message: 'Network request failed',
        url: (this as XMLHttpRequest & { _devConsoleUrl: string })._devConsoleUrl,
      })
    })
    return (originalXHRSend as Function).apply(this, args)
  }
}

export function useDevConsole() {
  const filteredEntries = computed(() => entries.value.filter(e => filters.value[e.type]))

  const errorCount = computed(() => entries.value.length)

  function clear() {
    entries.value = []
  }

  function toggleFilter(type: ConsoleEntryType) {
    filters.value[type] = !filters.value[type]
  }

  function copyEntry(entry: ConsoleEntry): string {
    const time = entry.timestamp.toLocaleTimeString()
    const msg = entry.url ? `${entry.message} | ${entry.url}` : entry.message
    return `[${time}] [${entry.source}] [${entry.type}] ${msg}`
  }

  function copyAll(): string {
    return filteredEntries.value.map(copyEntry).join('\n\n')
  }

  return {
    entries: filteredEntries,
    errorCount,
    filters,
    isExpanded,
    install,
    clear,
    toggleFilter,
    copyEntry,
    copyAll,
  }
}
